
import { useState } from 'react';
import { User, Twitter, Trophy, TrendingUp, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface BroUser {
  id: string;
  telegram_id: string;
  brocode: string;
  rank: string;
  bro_score: number;
  is_brometheus: boolean;
  twitter_handle?: string;
}

interface ProfileProps {
  user: BroUser;
}

const MiniAppProfile = ({ user }: ProfileProps) => {
  const [twitterHandle, setTwitterHandle] = useState(user.twitter_handle || '');
  const [isLinking, setIsLinking] = useState(false);
  const [linkingError, setLinkingError] = useState<string | null>(null);
  const [linkingSuccess, setLinkingSuccess] = useState(false);

  const linkTwitter = async () => {
    if (!twitterHandle.trim()) {
      setLinkingError('Please enter your Twitter handle');
      return;
    }

    setIsLinking(true);
    setLinkingError(null);
    setLinkingSuccess(false);

    try {
      const cleanHandle = twitterHandle.replace('@', '').trim();
      
      if (cleanHandle.length < 2) {
        setLinkingError('Please enter a valid Twitter handle');
        return;
      }

      // Check if handle is already taken
      const { data: existingHandle } = await supabase
        .from('initiated_bros')
        .select('id, brocode')
        .eq('twitter_handle', cleanHandle)
        .neq('id', user.id)
        .single();

      if (existingHandle) {
        setLinkingError(`Twitter handle @${cleanHandle} is already linked to ${existingHandle.brocode}`);
        return;
      }

      // Update user's Twitter handle
      const { error: updateError } = await supabase
        .from('initiated_bros')
        .update({ twitter_handle: cleanHandle })
        .eq('id', user.id);

      if (updateError) {
        console.error('Error linking Twitter:', updateError);
        setLinkingError('Failed to link Twitter handle. Please try again.');
        return;
      }

      // Award linking bonus
      await supabase
        .from('bro_scores')
        .insert({
          user_id: user.id,
          action: 'twitter_link',
          value: 10,
          metadata: { twitter_handle: cleanHandle }
        });

      // Update local user object
      user.twitter_handle = cleanHandle;
      setLinkingSuccess(true);
      setTwitterHandle('');
      
      console.log(`Successfully linked Twitter handle: @${cleanHandle}`);
    } catch (error) {
      console.error('Error in linkTwitter:', error);
      setLinkingError('An unexpected error occurred while linking Twitter.');
    } finally {
      setIsLinking(false);
    }
  };

  const getRankColor = (rank: string) => {
    switch (rank.toLowerCase()) {
      case 'brometheus':
        return 'text-yellow-400';
      case 'sigma':
        return 'text-purple-400';
      case 'alpha':
        return 'text-blue-400';
      case 'beta':
        return 'text-green-400';
      default:
        return 'text-foreground';
    }
  };

  const achievements = [
    { name: 'First Tweet', desc: 'Posted your first tweet', unlocked: true },
    { name: 'Consistent Shill', desc: 'Posted for 7 days straight', unlocked: true },
    { name: 'Challenge Master', desc: 'Won 5 wagers', unlocked: false },
    { name: 'Top Performer', desc: 'Reached top 10 leaderboard', unlocked: false },
  ];

  return (
    <div className="p-4 space-y-6">
      {/* Profile Header */}
      <div className="bg-secondary rounded-lg p-6 border border-border text-center">
        <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="text-primary-foreground" size={40} />
        </div>
        
        <h2 className="text-2xl font-bold text-foreground mb-2">{user.brocode}</h2>
        <div className={`text-lg font-bold ${getRankColor(user.rank)} mb-1`}>
          {user.rank}
          {user.is_brometheus && ' 👑'}
        </div>
        
        <div className="flex justify-center space-x-6 mt-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{user.bro_score}</div>
            <div className="text-muted-foreground text-sm">BroScore</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">0</div>
            <div className="text-muted-foreground text-sm">Wagers Won</div>
          </div>
        </div>
      </div>

      {/* Twitter Integration */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <Twitter className="mr-2" size={18} />
          Twitter Account
        </h3>
        
        {user.twitter_handle ? (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-foreground font-medium">@{user.twitter_handle}</div>
              <div className="text-muted-foreground text-sm">Linked & verified</div>
            </div>
            <div className="text-green-400">✓</div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              Link your Twitter to earn bonus points for engagement
            </p>
            
            {linkingSuccess && (
              <div className="bg-green-600 bg-opacity-20 border border-green-400 rounded p-2 text-green-400 text-sm">
                ✅ Twitter account linked successfully! +10 BroScore awarded.
              </div>
            )}
            
            {linkingError && (
              <div className="bg-red-600 bg-opacity-20 border border-red-400 rounded p-2 text-red-400 text-sm">
                ❌ {linkingError}
              </div>
            )}
            
            <div className="flex gap-2">
              <input
                type="text"
                value={twitterHandle}
                onChange={(e) => {
                  setTwitterHandle(e.target.value);
                  setLinkingError(null);
                  setLinkingSuccess(false);
                }}
                placeholder="@username"
                className="flex-1 bg-background border border-border rounded px-3 py-2 text-foreground text-sm"
                disabled={isLinking}
              />
              <button
                onClick={linkTwitter}
                disabled={isLinking || !twitterHandle.trim()}
                className="bg-blue-500 text-white px-4 py-2 rounded font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLinking ? 'Linking...' : 'Link'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Achievements */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <Trophy className="mr-2" size={18} />
          Achievements
        </h3>
        
        <div className="space-y-3">
          {achievements.map((achievement, index) => (
            <div key={index} className={`flex items-center justify-between p-3 rounded ${
              achievement.unlocked ? 'bg-background border border-border' : 'bg-muted border border-border'
            }`}>
              <div>
                <div className={`font-medium text-sm ${
                  achievement.unlocked ? 'text-foreground' : 'text-gray-400'
                }`}>
                  {achievement.name}
                </div>
                <div className={`text-xs ${
                  achievement.unlocked ? 'text-muted-foreground' : 'text-gray-500'
                }`}>
                  {achievement.desc}
                </div>
              </div>
              <div className={`text-xl ${
                achievement.unlocked ? 'text-yellow-400' : 'text-gray-600'
              }`}>
                {achievement.unlocked ? '🏆' : '🔒'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <TrendingUp className="mr-2" size={18} />
          Statistics
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">0</div>
            <div className="text-muted-foreground text-xs">Tweets This Week</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">0</div>
            <div className="text-muted-foreground text-xs">Active Wagers</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">#{Math.floor(Math.random() * 100) + 1}</div>
            <div className="text-muted-foreground text-xs">Leaderboard Rank</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-foreground">0</div>
            <div className="text-muted-foreground text-xs">Streak Days</div>
          </div>
        </div>
      </div>

      <div className="bg-secondary rounded-lg p-4 border border-border">
        <h3 className="text-foreground font-bold mb-3 flex items-center">
          <Settings className="mr-2" size={18} />
          Settings
        </h3>
        
        <div className="space-y-2">
          <button className="w-full text-left text-foreground hover:text-muted-foreground py-2 text-sm">
            Notification Preferences
          </button>
          <button className="w-full text-left text-foreground hover:text-muted-foreground py-2 text-sm">
            Privacy Settings
          </button>
          <button className="w-full text-left text-red-400 hover:text-red-300 py-2 text-sm">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default MiniAppProfile;
